import React, { useState, useEffect } from 'react';
import { List, ListItem, ListItemIcon, ListItemText, Toolbar, Typography, Box, IconButton, Drawer, Divider, useMediaQuery } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import Container from '@mui/material/Container';
import Avatar from '@mui/material/Avatar';
import MenuItem from '@mui/material/MenuItem';
import { useNavigate } from 'react-router';
import { useProfile } from 'context';
import { useAuth } from 'hooks/useAuth';
import { getTheme } from 'config/theme';
import noAvatar from 'assets/images/no-avatar.png';
import hiveLogo from 'assets/images/hive_logo.png';
import { getAvatarUrl } from 'api/utils';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grow from '@mui/material/Grow';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import MenuList from '@mui/material/MenuList';
import { StyledAppBar, LogoContainer, NavButton, UserSection } from './styled'
import { 
  Home as HomeIcon, 
  Business as BusinessIcon, 
  Person as PersonIcon, 
  BookOnline as BookOnlineIcon, 
  Logout as LogoutIcon,
  Info as InfoIcon,
  HelpCenter as HelpCenterIcon
} from '@mui/icons-material';


function MainNavBar() {
  const { profile } = useProfile();
  const { doLogout } = useAuth();
  const [theme] = useState(getTheme(null));
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const staffRole = profile?.staff?.role || null;
  // Get current location to determine active page
  const location = window.location.pathname;

  const publicPages = ['Spaces', 'Communities'];
  const commonPages = ['Home', 'Spaces', 'Community'];
  const staffPages = ['Spaces', 'Communities', 'Staff'];
  const pages = profile 
    ? (profile?.staff?.is_staff ? staffPages : commonPages)
    : publicPages;
  let settings = profile ? ['Bookings', 'Profile', 'About Us', 'Help Center', 'Logout'] : ['Signup'];

  if (profile && (profile?.staff?.is_staff || staffRole == 'AD' || staffRole == 'SA')) {
    settings = ['Profile', 'Logout'];
  }

  const getIconForPage = (page) => {
    switch (page) {
      case 'Home': return <HomeIcon />;
      case 'Find space': return <BusinessIcon />;
      default: return <BusinessIcon />;
    }
  };

  const getIconForSetting = (setting) => {
    switch (setting) {
      case 'Profile': return <PersonIcon />;
      case 'Bookings': return <BookOnlineIcon />;
      case 'Logout': return <LogoutIcon />;
      case 'About Us': return <InfoIcon />;
      case 'Help Center': return <HelpCenterIcon />;
      default: return <PersonIcon />;
    }
  };

  const avatar = getAvatarUrl(profile, noAvatar);

  const [,setAnchorElNav] = useState(null);
  const [open, setOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = React.useRef(null);

  // Handle scroll
  useEffect(() => {
    const handleScroll = () => {
      if (open) {
        setOpen(false);
      }
      if (userMenuOpen) {
        setUserMenuOpen(false);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [open, userMenuOpen]);

  const handleUserMenuToggle = () => {
    setUserMenuOpen((prevOpen) => !prevOpen);
  };

  const handleUserMenuClose = (event) => {
    if (userMenuRef.current && userMenuRef.current.contains(event?.target)) {
      return;
    }
    setUserMenuOpen(false);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  // Get page path based on menu item
  function getPagePath(target) {
    if (target === 'Home') {
      return profile?.staff?.is_staff ? '/manage/venues' : '/dashboard';
    }
    if (target === 'Community') {
      return '/communities';
    }
    if (target === 'Communities') {
      return profile?.staff?.is_staff ? '/manage/communities' : '/communities';
    }
    if (target === 'Events') {
      return '/events';
    }
    if (target === 'Spaces') {
      if (profile?.staff?.is_staff) {
        return '/manage/venues';
      } else if (profile) {
        return '/booking-find';
      } else {
        // Non-logged-in users go to main page
        return '/';
      }
    }
    if (target === 'Staff') {
      return staffRole == 'AD' || staffRole == 'SA' ? '/manage/staff' : '/profile';
    }
    if (target === 'Book') {
      return '/booking-find';
    }
    return '';
  }
  
  // Check if a menu item is active based on current location
  function isActive(target) {
    const path = getPagePath(target);
    return location.startsWith(path);
  }

  function navigateMenu(target) {
    handleCloseNavMenu();
    const path = getPagePath(target);
    navigate(path);
  }

  function navigateSetting(setting) {
    setOpen(false);
    if (setting === 'Profile') navigate('/profile');
    if (setting === 'Account') navigate('/account');
    if (setting === 'Bookings') navigate('/bookings');
    if (setting === 'Signup') navigate('/signup');
    if (setting === 'Manage') navigate('/manage/venues');
    if (setting === 'About Us') navigate('/about-us');
    if (setting === 'Help Center') navigate('/contact-us');
    if (setting === 'Logout') doLogout();
  }

  return (
    <StyledAppBar position="sticky">
      <Container maxWidth="xl">
        <Toolbar
          disableGutters
          sx={{
            py: 1,
          }}
        >
          {isMobile && (
            <IconButton
              edge="start"
              color="inherit"
              aria-label="menu"
              onClick={() => setMobileMenuOpen(true)}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}
          <LogoContainer onClick={() => navigateMenu('Home')}>
            <Box
              component="img"
              src={hiveLogo}
              alt="Hivespace Logo"
              sx={{
                height: isMobile ? 24 : 32,
                width: 'auto'
              }}
            />
            <Typography
              variant="h6"
              sx={{
                fontFamily: 'monospace',
                fontWeight: 700,
                letterSpacing: '.3rem',
                background: 'linear-gradient(45deg, #FFA000, #FF6D00)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                fontSize: isMobile ? '1rem' : '1.25rem',
              }}
            >
              HIVESPACE
            </Typography>
          </LogoContainer>

          {/* Desktop Navigation */}
          <Box sx={{
            flexGrow: 1,
            display: { xs: 'none', md: 'flex' },
            justifyContent: 'center',
            gap: 2
          }}>
            {pages.map((page) => (
              <NavButton 
                key={page} 
                onClick={() => navigateMenu(page)}
                sx={{
                  position: 'relative',
                  '&::after': isActive(page) ? {
                    content: '""',
                    position: 'absolute',
                    bottom: -8,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '60%',
                    height: 3,
                    backgroundColor: 'primary.main',
                    borderRadius: 3
                  } : {}
                }}
              >
                {page}
              </NavButton>
            ))}
          </Box>

          {/* User Section - Desktop Only */}
          {!isMobile ? (
            profile ? (
              <UserSection>
                <Typography sx={{ fontSize: '0.875rem', color: 'text.primary' }}>
                  Hello, {profile?.first_name}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <IconButton
                    ref={userMenuRef}
                    onClick={handleUserMenuToggle}
                    sx={{ 
                      p: 1,
                      color: 'text.primary',
                      transition: 'all 0.2s',
                      '&:hover': {
                        color: 'primary.main',
                        backgroundColor: 'action.hover'
                      }
                    }}
                  >
                    <MenuIcon />
                  </IconButton>
                </Box>
                
                {/* Enhanced Desktop User Menu Popper */}
                <Popper
                  open={userMenuOpen}
                  anchorEl={userMenuRef.current}
                  placement="bottom-end"
                  transition
                  disablePortal
                  sx={{ zIndex: 1301 }}
                >
                  {({ TransitionProps }) => (
                    <Grow
                      {...TransitionProps}
                      style={{ transformOrigin: 'top right' }}
                    >
                      <Paper sx={{
                        mt: 1.5,
                        minWidth: 240,
                        boxShadow: 6,
                        borderRadius: 2,
                        overflow: 'hidden'
                      }}>
                        <ClickAwayListener onClickAway={handleUserMenuClose}>
                          <Box>
                            {/* Profile header in the menu */}
                            <Box
                              sx={{
                                p: 2,
                                backgroundColor: 'grey.50',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2,
                                borderBottom: 1,
                                borderColor: 'divider'
                              }}
                            >
                              <Avatar
                                src={avatar}
                                sx={{ width: 48, height: 48 }}
                              />
                              <Box>
                                <Typography variant="subtitle1" fontWeight={600}>
                                  {profile?.first_name} {profile?.last_name}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {profile?.alias}
                                </Typography>
                              </Box>
                            </Box>
                            
                            {/* Menu items with icons */}
                            <MenuList sx={{ py: 1 }}>
                              {settings.map((setting) => (
                                <MenuItem
                                  key={setting}
                                  onClick={() => {
                                    navigateSetting(setting);
                                    handleUserMenuClose();
                                  }}
                                  sx={{
                                    py: 1.5,
                                    px: 2.5,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 2,
                                    '&:hover': {
                                      backgroundColor: 'action.hover',
                                    },
                                    color: setting === 'Logout' ? 'error.main' : 'inherit'
                                  }}
                                >
                                  <Box sx={{ 
                                    display: 'flex', 
                                    color: setting === 'Logout' ? 'error.main' : 'primary.main'
                                  }}>
                                    {getIconForSetting(setting)}
                                  </Box>
                                  <Typography>{setting}</Typography>
                                </MenuItem>
                              ))}
                            </MenuList>
                          </Box>
                        </ClickAwayListener>
                      </Paper>
                    </Grow>
                  )}
                </Popper>
              </UserSection>
            ) : (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <NavButton onClick={() => navigate('/signup')}>
                  Sign Up
                </NavButton>
                <NavButton onClick={() => navigate('/login')} sx={{ 
                  backgroundColor: 'primary.main',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'primary.dark'
                  }
                }}>
                  Login
                </NavButton>
              </Box>
            )
          ) : (
            <IconButton
              edge="end"
              onClick={() => setMobileMenuOpen(true)}
              sx={{ ml: 'auto' }}
            >
              <MenuIcon />
            </IconButton>
          )}

          {/* Mobile Drawer */}
          <Drawer
            anchor="right"
            open={mobileMenuOpen}
            onClose={() => setMobileMenuOpen(false)}
          >
            <Box
              sx={{
                width: 280,
                height: '100%',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              {/* User Profile Section in Drawer - Only show if logged in */}
              {profile && (
                <Box
                  sx={{
                    p: 2,
                    backgroundColor: 'grey.50',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    borderBottom: 1,
                    borderColor: 'divider'
                  }}
                >
                  <Avatar
                    src={avatar}
                    sx={{ width: 48, height: 48 }}
                  />
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {profile?.first_name} {profile?.last_name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {profile?.alias}
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* Non-logged-in header */}
              {!profile && (
                <Box
                  sx={{
                    p: 2,
                    backgroundColor: 'grey.50',
                    borderBottom: 1,
                    borderColor: 'divider'
                  }}
                >
                  <Typography variant="h6" fontWeight={600}>
                    HIVESPACE
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Explore our spaces and communities
                  </Typography>
                </Box>
              )}

              {/* Navigation Items */}
              <List sx={{ flexGrow: 1, pt: 2 }}>
                {pages.map((page) => (
                  <ListItem
                    button
                    key={page}
                    onClick={() => {
                      navigateMenu(page);
                      setMobileMenuOpen(false);
                    }}
                  >
                    <ListItemIcon>
                      {getIconForPage(page)}
                    </ListItemIcon>
                    <ListItemText primary={page} />
                  </ListItem>
                ))}
              </List>

              {/* Settings/Account Section */}
              <Divider />
              <List>
                {settings.map((setting) => (
                  <ListItem
                    button
                    key={setting}
                    onClick={() => {
                      navigateSetting(setting);
                      setMobileMenuOpen(false);
                    }}
                    sx={{
                      color: setting === 'Logout' ? 'error.main' : 'inherit'
                    }}
                  >
                    <ListItemIcon sx={{
                      color: setting === 'Logout' ? 'error.main' : 'inherit'
                    }}>
                      {getIconForSetting(setting)}
                    </ListItemIcon>
                    <ListItemText primary={setting} />
                  </ListItem>
                ))}
              </List>
            </Box>
          </Drawer>
        </Toolbar>
      </Container>
    </StyledAppBar>
  );
}

export default MainNavBar;